/**
 * Settings > Your account (DEC-198): Profile and Appearance, for every profile.
 *
 * Profile is read-only: there is no endpoint to change a name, and the profile (role) is
 * set by a workspace admin (§38). Appearance is the theme (DEC-130), kept per device.
 * No em-dashes (DEC-069).
 */
import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { currentTheme, followSystemTheme, applyTheme, storedTheme, subscribe } from '../../theme'
import { PROFILE_LABEL } from './settingsSections'

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-panel border border-hairline rounded-md overflow-hidden mb-4">
      <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
        {title}
      </div>
      <div className="px-4">{children}</div>
    </div>
  )
}

function Field({ label, value, note }: { label: string; value: React.ReactNode; note?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-hairline last:border-b-0">
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-body">{label}</div>
        {note && <div className="text-[12px] text-faint mt-0.5">{note}</div>}
      </div>
      <div className="text-[13px] text-ink text-right min-w-0 break-words">{value}</div>
    </div>
  )
}

export function ProfileSection() {
  const { claims } = useAuth()
  const profile = claims?.profile ?? null
  return (
    <Card title="Profile">
      <Field label="Name" value={claims?.name ?? <span className="text-muted">Not set</span>} />
      <Field label="Email" value={claims?.email ?? <span className="text-muted">Not set</span>} />
      <Field
        label="Profile"
        value={profile ? PROFILE_LABEL[profile] : 'Admin'}
        note="Decides which report views you can open. A workspace admin sets it."
      />
      <Field label="Workspace" value={claims?.tenant_name ?? 'Workspace'} />
    </Card>
  )
}

type Choice = 'light' | 'dark' | 'system'

export function AppearanceSection() {
  const [choice, setChoice] = useState<Choice>(() => storedTheme() ?? 'system')
  const [, setTheme] = useState(currentTheme())

  // The top bar switch (DEC-130) also changes the theme; keep this control in step.
  useEffect(() => subscribe((t) => {
    setTheme(t)
    setChoice(storedTheme() ?? 'system')
  }), [])

  function pick(c: Choice) {
    if (c === 'system') followSystemTheme()
    else applyTheme(c)
    setChoice(c)
  }

  const options: [Choice, string][] = [
    ['light', 'Light'],
    ['dark', 'Dark'],
    ['system', 'Match my computer'],
  ]

  return (
    <Card title="Appearance">
      <div className="flex items-center justify-between gap-4 py-3 flex-wrap">
        <div className="min-w-0">
          <p id="theme-label" className="text-[13px] font-medium text-body">Theme</p>
          <p className="text-[12px] text-faint mt-0.5">Saved on this device only.</p>
        </div>
        <div role="radiogroup" aria-labelledby="theme-label" className="flex flex-none gap-1 p-1 bg-fill-subtle rounded-lg">
          {options.map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={choice === v}
              onClick={() => pick(v)}
              className={`px-3 py-1.5 rounded-md text-[12px] font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:shadow-focus ${
                choice === v ? 'bg-surface-active text-ink' : 'text-muted hover:text-body'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </Card>
  )
}
